import '@operato/input/ox-select.js'
import '@operato/input/ox-input-code.js'
import '@operato/markdown/ox-markdown.js'
import './function-box-options-builder.js'

import { LitElement, html, css } from 'lit'
import { customElement, property, state, query } from 'lit/decorators.js'
import { CommonHeaderStyles } from '@operato/styles'
import { i18next } from '@operato/i18n'

import { Mapping } from '../types.js'
import { FunctionList } from '../functions/index.js'

@customElement('function-box-popup')
export class FunctionBoxPopup extends LitElement {
  static styles = [
    CommonHeaderStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        border: 1px solid #ccc;
        background: #f9f9f9;
        overflow: hidden;
      }

      div#body {
        display: flex;
        flex-direction: column;
        height: 100%;
        border: 1px solid #ccc;
        background-color: var(--md-sys-color-surface);
        color: var(--md-sys-color-primary);
        overflow: hidden;
      }

      div#function {
        display: flex;
        flex-direction: row;
        gap: var(--spacing-small, 4px);
        padding: 10px;
      }

      div#detail {
        flex: 1;
        display: flex;
        flex-direction: row;
        gap: var(--spacing-small, 4px);
        background-color: var(--md-sys-color-surface);
        color: var(--md-sys-color-primary);
        overflow: hidden;
      }

      div#variables {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: var(--spacing-small, 4px);
      }

      div#variables label {
        background-color: var(--md-sys-color-on-primary-container);
        color: var(--md-sys-color-primary-container);
      }

      .variable-list {
        display: flex;
        flex-direction: row;
        align-items: center;
        transition: transform 0.3s ease-in-out;
      }

      .variable-list .padding {
        flex: 1;
      }

      div#inputs {
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      div#returns {
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      div#editor {
        flex: 4;
        display: flex;
        flex-direction: column;
        gap: var(--spacing-small, 4px);
        overflow: hidden;
      }

      function-box-options-builder {
        min-height: 200px;
      }

      ox-markdown {
        flex: 1;
        padding: var(--spacing-medium, 8px);
        background-color: var(--md-sys-color-surface-variant);
        overflow: auto;
      }

      ox-select {
        flex: 1;
      }
    `
  ]

  @property({ type: Object }) mapping!: Mapping

  @query('#select-function') mappingFunction!: HTMLSelectElement

  render() {
    var { mappingFunction = 'passthrough', inputs, outputs, parameters = {} } = this.mapping
    const { help = '', specifications = [] } = FunctionList.find(func => func.name == mappingFunction) || {}

    const helpDataUrl = `data:text/markdown;base64,${window.btoa(unescape(encodeURIComponent(help)))}`

    return html`
      <div class="header">
        <div class="title">Function Box</div>
      </div>

      <div id="body">
        <div id="function">
          <label for="select-function">select function</label>
          <ox-select
            id="select-function"
            .value=${mappingFunction || 'passthrough'}
            @change=${(e: Event) => {
              e.stopPropagation()

              let val = (e.target as HTMLInputElement).value

              this.mapping = {
                ...this.mapping,
                mappingFunction: val?.length > 0 ? val : 'passthrough'
              }
            }}
          >
            <ox-popup-list with-search align-left>
              ${FunctionList.map(
                func => html`
                  <div option value=${func.name} ?selected=${func.name == mappingFunction}>${func.label}</div>
                `
              )}
            </ox-popup-list>
          </ox-select>
        </div>

        <div id="detail">
          <div id="variables">
            <div id="inputs">
              <label>inputs</label>
              ${inputs.map(
                (input, index) => html`
                  <div class="variable-list">
                    ${input}
                    <div class="padding"></div>
                    <md-icon @click=${() => this.swapVariablePosition('in', index, 'up')}>arrow_upward</md-icon
                    ><md-icon @click=${() => this.swapVariablePosition('in', index, 'down')}>arrow_downward</md-icon>
                  </div>
                `
              )}
            </div>
            <div id="returns">
              <label>returns</label>
              ${outputs.map(
                (output, index) => html`
                  <div class="variable-list">
                    ${output}
                    <div class="padding"></div>
                    <md-icon @click=${() => this.swapVariablePosition('out', index, 'up')}>arrow_upward</md-icon
                    ><md-icon @click=${() => this.swapVariablePosition('out', index, 'down')}>arrow_downward</md-icon>
                  </div>
                `
              )}
            </div>
          </div>
          <div id="editor">
            <function-box-options-builder
              .value=${parameters}
              .props=${specifications}
              @property-change=${this.updateOptions}
            ></function-box-options-builder>
            <ox-markdown src=${helpDataUrl} .toc=${false}></ox-markdown>
          </div>
        </div>
      </div>

      <div class="footer">
        <div filler></div>
        <button @click=${this.updateMapping.bind(this)} done><md-icon>save</md-icon>${i18next.t('button.save')}</button>
      </div>
    `
  }

  private swapVariablePosition(inout: 'in' | 'out', index: number, direction: 'up' | 'down') {
    const list = inout === 'in' ? this.mapping.inputs : this.mapping.outputs

    if ((direction === 'up' && index === 0) || (direction === 'down' && index === list.length - 1)) {
      return // 첫 번째 요소를 위로 올리거나, 마지막 요소를 아래로 내릴 수 없음
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1
    const movingElement = this.shadowRoot!.querySelectorAll(`.variable-list`)[index] as HTMLElement
    const targetElement = this.shadowRoot!.querySelectorAll(`.variable-list`)[newIndex] as HTMLElement

    if (!movingElement || !targetElement) return

    // 현재 위치 저장
    const movingRect = movingElement.getBoundingClientRect()
    const targetRect = targetElement.getBoundingClientRect()

    const deltaY = targetRect.top - movingRect.top

    // 애니메이션을 위해 transform 적용
    movingElement.style.transition = 'transform 0.3s ease-in-out'
    movingElement.style.transform = `translateY(${deltaY}px)`

    // 애니메이션이 끝나면 실제로 데이터 순서 변경 및 리렌더링
    setTimeout(() => {
      const temp = list[index]
      list[index] = list[newIndex]
      list[newIndex] = temp

      // 스타일 초기화
      movingElement.style.transition = ''
      movingElement.style.transform = ''

      this.requestUpdate()
    }, 300) // 300ms는 transition 속도와 동일
  }

  private updateOptions(e: Event) {
    e.stopPropagation()

    const parameters = this.mapping.parameters || {}

    this.mapping.parameters = {
      ...parameters,
      ...(e as CustomEvent).detail
    }
  }

  private updateMapping() {
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: this.mapping
      })
    )
  }
}
